"use client";

import { c as _c } from "react/compiler-runtime";
import { format } from "date-fns";
import { Loader2, MessageSquare, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useOrgShares } from "@/hooks/useChats";
import { cn } from "@/lib/utils";
const ROW_HEIGHT_HEADER = 38;
const ROW_HEIGHT_SHARE = 44;
const ROW_HEIGHT_EMPTY = 180;
const ROW_HEIGHT_LOADER = 48;
const CLOSE_ANIMATION_MS = 150;
interface OrgShareItem {
  id: string;
  title: string;
  model: string;
  createdAt: string;
  updatedAt: string;
}
type SearchRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "share";
  key: string;
  share: OrgShareItem;
} | {
  type: "empty";
  key: string;
  message: string;
} | {
  type: "loader";
  key: string;
  message: string;
};
interface OrgSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShareSelect?: (shareId: string) => void;
  organizationId: string;
}
interface SearchRowProps {
  rows: SearchRow[];
  onShareSelect: (shareId: string) => void;
}
function useDebouncedValue(value, delay) {
  const $ = _c(4);
  const [debouncedValue, setDebouncedValue] = useState(value);
  let t0;
  let t1;
  if ($[0] !== delay || $[1] !== value) {
    t0 = () => {
      const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
      return () => window.clearTimeout(timeout);
    };
    t1 = [delay, value];
    $[0] = delay;
    $[1] = value;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return debouncedValue;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  }
  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  }
  if (diffInHours < 48) {
    return "Yesterday";
  }
  return format(date, "MMM d");
}
function groupSharesByDate(shares: OrgShareItem[], now: Date) {
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(now);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as OrgShareItem[],
    yesterday: [] as OrgShareItem[],
    lastWeek: [] as OrgShareItem[],
    older: [] as OrgShareItem[]
  };
  shares.forEach(share => {
    const shareDate = new Date(share.updatedAt);
    if (shareDate.toDateString() === today.toDateString()) {
      groups.today.push(share);
    } else if (shareDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(share);
    } else if (shareDate >= lastWeek) {
      groups.lastWeek.push(share);
    } else {
      groups.older.push(share);
    }
  });
  return groups;
}
function getSearchRowHeight(index: number, {
  rows
}: SearchRowProps) {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "loader") {
    return ROW_HEIGHT_LOADER;
  }
  if (row?.type === "empty") {
    return ROW_HEIGHT_EMPTY;
  }
  return ROW_HEIGHT_SHARE;
}
function SearchRowComponent(t0) {
  const $ = _c(38);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    onShareSelect
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "header") {
    let t1;
    if ($[0] !== row.title) {
      t1 = <div className="flex h-full items-end px-6 pb-2 text-xs font-medium text-muted-foreground">{row.title}</div>;
      $[0] = row.title;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== ariaAttributes || $[3] !== style || $[4] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[2] = ariaAttributes;
      $[3] = style;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  if (row.type === "loader") {
    let t1;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loader2 className="mr-2 size-4 animate-spin" />;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    let t2;
    if ($[7] !== row.message) {
      t2 = <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t1}{row.message}</div>;
      $[7] = row.message;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== ariaAttributes || $[10] !== style || $[11] !== t2) {
      t3 = <div {...ariaAttributes} style={style}>{t2}</div>;
      $[9] = ariaAttributes;
      $[10] = style;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    return t3;
  }
  if (row.type === "empty") {
    let t1;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <MessageSquare className="mb-3 size-8 opacity-60" />;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    let t2;
    if ($[14] !== row.message) {
      t2 = <div className="flex h-full flex-col items-center justify-center px-6 text-center text-sm text-muted-foreground">{t1}{row.message}</div>;
      $[14] = row.message;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    let t3;
    if ($[16] !== ariaAttributes || $[17] !== style || $[18] !== t2) {
      t3 = <div {...ariaAttributes} style={style}>{t2}</div>;
      $[16] = ariaAttributes;
      $[17] = style;
      $[18] = t2;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    return t3;
  }
  let t1;
  if ($[20] !== onShareSelect || $[21] !== row.share.id) {
    t1 = () => onShareSelect(row.share.id);
    $[20] = onShareSelect;
    $[21] = row.share.id;
    $[22] = t1;
  } else {
    t1 = $[22];
  }
  let t2;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MessageSquare className="size-4 shrink-0 text-muted-foreground" />;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== row.share.title) {
    t3 = <span className="min-w-0 flex-1 truncate font-medium">{row.share.title}</span>;
    $[24] = row.share.title;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== row.share.updatedAt) {
    t4 = formatDate(row.share.updatedAt);
    $[26] = row.share.updatedAt;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== t4) {
    t5 = <span className="shrink-0 text-xs text-muted-foreground">{t4}</span>;
    $[28] = t4;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== t1 || $[31] !== t3 || $[32] !== t5) {
    t6 = <div className="px-4"><button type="button" onClick={t1} className="flex h-11 w-full items-center gap-3 rounded-md px-2 text-left text-sm transition-colors hover:bg-muted/60">{t2}{t3}{t5}</button></div>;
    $[30] = t1;
    $[31] = t3;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== ariaAttributes || $[35] !== style || $[36] !== t6) {
    t7 = <div {...ariaAttributes} style={style}>{t6}</div>;
    $[34] = ariaAttributes;
    $[35] = style;
    $[36] = t6;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  return t7;
}
export function OrgSearchDialog({
  open,
  onOpenChange,
  onShareSelect,
  organizationId
}: OrgSearchDialogProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const nowRef = useRef<Date>(new Date());
  const {
    data: orgSharesData,
    isLoading
  } = useOrgShares(organizationId);
  useEffect(() => {
    if (open) {
      nowRef.current = new Date();
    } else {
      setQuery("");
    }
  }, [open]);
  useEffect(() => {
    if (!open) {
      return;
    }
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timeout);
  }, [open]);
  const shares = useMemo<OrgShareItem[]>(() => {
    const all = orgSharesData?.shares ?? [];
    const q = debouncedQuery.trim().toLowerCase();
    return q ? all.filter(s => s.title.toLowerCase().includes(q)) : all;
  }, [orgSharesData?.shares, debouncedQuery]);
  const rows = useMemo<SearchRow[]>(() => {
    const groups = groupSharesByDate(shares, nowRef.current);
    const nextRows: SearchRow[] = [];
    [{
      title: "Today",
      shares: groups.today
    }, {
      title: "Yesterday",
      shares: groups.yesterday
    }, {
      title: "Previous 7 Days",
      shares: groups.lastWeek
    }, {
      title: "Older",
      shares: groups.older
    }].forEach(({
      title,
      shares: groupedShares
    }) => {
      if (groupedShares.length === 0) {
        return;
      }
      nextRows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedShares.forEach(share => {
        nextRows.push({
          type: "share",
          key: `share-${share.id}`,
          share
        });
      });
    });
    if (isLoading) {
      nextRows.push({
        type: "loader",
        key: "loader",
        message: "Loading shared chats..."
      });
    } else if (shares.length === 0) {
      nextRows.push({
        type: "empty",
        key: "empty",
        message: debouncedQuery.trim() ? "No matching chats" : "No shared chats in this organization"
      });
    }
    return nextRows;
  }, [shares, debouncedQuery, isLoading]);
  const closeAndSelectShare = useCallback((shareId: string) => {
    onOpenChange(false);
    setTimeout(() => {
      onShareSelect?.(shareId);
    }, CLOSE_ANIMATION_MS);
  }, [onShareSelect, onOpenChange]);
  const rowProps = useMemo<SearchRowProps>(() => ({
    rows,
    onShareSelect: closeAndSelectShare
  }), [closeAndSelectShare, rows]);
  return <Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent showCloseButton={false} className="grid h-[min(76vh,560px)] max-w-[min(720px,calc(100vw-2rem))] grid-rows-[4rem_minmax(0,1fr)] gap-0 overflow-hidden rounded-xl border-border bg-popover p-0 text-popover-foreground">
				<DialogTitle className="sr-only">Search organization chats</DialogTitle>
				<DialogDescription className="sr-only">
					Search through shared chats in this organization.
				</DialogDescription>
				<div className="flex h-16 items-center gap-3 border-b px-6">
					<Search className="size-4 shrink-0 text-muted-foreground" />
					<Input ref={inputRef} value={query} onChange={event => setQuery(event.target.value)} placeholder="Search shared chats..." className="h-full border-0 bg-transparent! px-0 text-base shadow-none focus-visible:ring-0" />
					<button type="button" aria-label="Close search" onClick={() => onOpenChange(false)} className="rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground">
						<X className="size-5" aria-hidden="true" />
					</button>
				</div>
				<div className="min-h-0 flex-1 py-2">
					<List className={cn("h-full min-h-0 w-full")} style={{
          height: "100%",
          width: "100%"
        }} rowComponent={SearchRowComponent} rowCount={rows.length} rowHeight={getSearchRowHeight} rowProps={rowProps} overscanCount={8} />
				</div>
			</DialogContent>
		</Dialog>;
}